/** @jsxImportSource @emotion/react */

import React, { useEffect, useRef, useState } from 'react';
import { getAttendanceType } from '@util/attendance';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import * as size from '@constant/size';
import * as color from '@constant/color';
import { ETheme } from '@datatype/type';
import {
  EApiLanguageCode,
  EApiAttendanceType,
  EApiStudyType,
  IClassroomGrammarProblem,
} from '@datatype/iyagigo/api-type';
import TextBlock from '@component/common/block/text-block';
import TextButton from '@design-system/text-button';
import Canvas from '@design-system/canvas';
import Chip from '@design-system/chip';
import ImageAudioButton from '@design-system/image-audio-button';
import { markBlanks, normalizeExplanation } from '@util/string';
import BottomContainer from '@component/common/architecture/bottom-container';
import Scroll, { ScrollContainer } from '@component/common/scroll';
import { usePlaySound } from '@hook/common/useSound';
import { useCheckAttendance, useRecordStudy } from '@hook/iyagigo/useStudent';
import { useActiveTime } from '@hook/common/useEvent';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { IS_IYAGIGO_MOBILE_VIEW } from '@config/environment';
import { IYAGIGO_PATH } from '@constant/iyagigo/route-path';
import { LOCAL_STORAGE_STUDENT_KEY } from '@config/auth';
import Gap from '@component/common/gap';
import { actions as modalActions } from '@store/slice/modal-slice';
import OtterTeacher, { type IPlaybackState } from '@component/common/otter-teacher';
import RemotionWatchBoard from '@component/common/remotion-watch-board';
import WatchControl from '@component/iyagigo/classroom/grammar-study/lecture-controls';
import { useModifyClassroomGrammarWorkbookMutation, useClassroomGrammarWorkbooksQuery } from '@hook/iyagigo/useApi';
import { groupClassroomGrammars } from '@util/classroom';
import { shuffleArrayWithSeed } from '@util/random';
import Line from '@component/common/line';

// ###################################
// GrammarStudy — 기초문법 학습 (watch → read → practice → 완료)
// - 라우트: /ig/classroom-grammar (URL: ?stage=noun&topic=noun-sentence&grammar=noun-desu)
// - character-study 패턴 미러 (카드/퀴즈/완료 phase, 정오답 사운드, BottomContainer)
// - 콘텐츠는 classroom grammar API에서 조회. URL의 stage/topic/grammar key로 현재 문법을 찾음
// ###################################

const Wrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  padding: ${size.PAGE_PADDING} ${size.PAGE_PADDING} 0 ${size.PAGE_PADDING};
  display: flex;
  flex-flow: column nowrap;
`;

const ContentContainer = styled.div`
  display: flex;
  flex: 1;
  flex-flow: column nowrap;
  width: 100%;
  //min-height: 100%;
`;

const BottomContentContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  height: 100%;
  gap: ${size.MARGIN_1};
  padding: 0 ${size.PAGE_PADDING} ${IS_IYAGIGO_MOBILE_VIEW ? '0px' : size.MARGIN_4} ${size.PAGE_PADDING};
`;

// 채점 후 상단 컨트롤 줄 (다음문제 우측 정렬) — cloze/character 패턴
const ControlRowContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
  width: 100%;
`;

const NavigationContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: ${size.MARGIN_1};
  width: 100%;
`;

const NAVIGATION_BUTTON_WIDTH = '80px';

// Canvas 내부 세로 정렬용 (설명/예문 공용) — 배경/테두리는 Canvas가 담당
const GrammarContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
`;

const GrammarFormContainer = styled.div`
  display: flex;
  flex-flow: row wrap;
  align-items: flex-end;
  padding: 5px;
  //border-radius: ${size.BORDER_RADIUS_2};
  background-color: ${color.BROWN_5};
`;

// 수달+칠판을 하나의 매체로 감싼다. 테두리·둥근모서리·클리핑을 여기서 관리(자식은 모서리 신경 X).
const GrammarWatchContainer = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  overflow: hidden;
  border-radius: ${size.MARGIN_2};
`;

const TeacherContainer = styled.div`
  flex-shrink: 0;
  width: 100%;
`;

const ExamplesContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 10px;
  margin-top: ${size.MARGIN_2};
`;

const ExampleContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  width: 100%;
`;

const ExampleSentenceContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const VocabulariesContainer = styled.div`
  display: flex;
  flex-flow: row wrap;
  align-items: flex-end;
  gap: 10px;
  padding: 0 0 0 5px;
  border-left: ${size.BORDER_WIDTH_1} solid ${color.BROWN_1};
  width: 100%;
`;

const ExampleVocabularyContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: flex-end;
  gap: 3px;
`;

// 문제 풀이 단어 영역 — 카드 하단 좌측 정렬. '어휘' 라벨 뒤에 단어를 가로로 나열한다.
const QuizVocabularySection = styled.div`
  display: flex;
  flex-flow: row wrap;
  align-items: flex-end;
  gap: 5px 10px;
  width: 100%;
  padding-top: 5px;
`;

// 단어 한 줄 — 단어(한자 위 후리가나) + 뜻 (가로 나열)
const QuizVocabularyItem = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: flex-end;
  gap: 4px;
`;

const ChoicesContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 8px;
  width: 100%;
`;

const ChoiceContainer = styled.div<{ $borderColor: string; $answered: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 5px 5px;
  border-radius: ${size.BORDER_RADIUS_1};
  cursor: ${({ $answered }) => ($answered ? 'default' : 'pointer')};
  background-color: ${color.WHITE_1};
  border: 1px solid ${({ $borderColor }) => $borderColor};
`;

// 선택지 테두리 색 (character-study getChoiceBorderColor 패턴)
const getChoiceBorderColor = (isAnswerView: boolean, isSelected: boolean, isCorrect: boolean): string => {
  if (isAnswerView) {
    if (isCorrect) return color.GREEN_4;
    if (isSelected) return color.RED_3;
  } else if (isSelected) {
    return color.YELLOW_3;
  }
  return color.GRAY_4;
};

type TStudyPhase = 'watch' | 'read' | 'practice';

interface IProps {
  worksheet?: {
    stageKey: string;
    topicKey: string;
    grammarKey: string;
    languageCode: EApiLanguageCode;
    onComplete: (correctCount: number, totalCount: number) => void;
  };
  // 헤더 프로그레스바 갱신용 (character-study와 동일 시그니처)
  onProgress?: (progress: { current: number; total: number }) => void;
}

const GrammarStudy: React.FC<IProps> = ({ onProgress, worksheet }) => {
  const navigate = useNavigate();
  const dispatch = useTypedDispatch();
  const location = useLocation();
  const playSound = usePlaySound();
  const checkAttendance = useCheckAttendance();
  const recordStudy = useRecordStudy();
  const studyLanguage = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage);
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? '';

  // ############### URL 파라미터 ###############
  const searchParams = new URLSearchParams(location.search);
  const stageKey = worksheet?.stageKey ?? searchParams.get('stage');
  const topicKey = worksheet?.topicKey ?? searchParams.get('topic');
  const grammarKey = worksheet?.grammarKey ?? searchParams.get('grammar');
  const language = searchParams.get('language');
  const languageCode =
    worksheet?.languageCode ??
    (language === EApiLanguageCode.ENGLISH.toLowerCase()
      ? EApiLanguageCode.ENGLISH
      : language === EApiLanguageCode.JAPANESE.toLowerCase()
        ? EApiLanguageCode.JAPANESE
        : (studyLanguage ?? EApiLanguageCode.JAPANESE));

  // ############### 콘텐츠 조회 ###############
  const { data: workbooksResponse, isLoading } = useClassroomGrammarWorkbooksQuery(languageCode);
  const modifyWorkbookMutation = useModifyClassroomGrammarWorkbookMutation(languageCode);
  const workbooks = workbooksResponse?.data ?? [];
  const stages = groupClassroomGrammars(workbooks.map((workbook) => workbook.classroomGrammar));
  const stage = stages.find((item) => item.key === stageKey) ?? null;
  const topic = stage?.topics.find((t) => t.key === topicKey) ?? null;
  const grammar = topic?.grammars.find((item) => item.grammarKey === grammarKey) ?? null;
  const grammarWorkbook = workbooks.find((workbook) => workbook.classroomGrammar.grammarKey === grammarKey);

  // ############### state ###############
  const [phase, setPhase] = useState<TStudyPhase>(worksheet ? 'read' : 'watch');
  const { getActiveTime, reset: resetActiveTime } = useActiveTime(true, phase === 'watch' ? 60_000 : 10_000);
  const [quizIndex, setQuizIndex] = useState<number>(0);
  const [selectedChoiceKey, setSelectedChoiceKey] = useState<string | null>(null);
  const [isAnswerView, setIsAnswerView] = useState<boolean>(false);
  const [correctCount, setCorrectCount] = useState<number>(0);
  const choiceShuffleSeedRef = useRef(Math.random().toString(36));
  const [watchPlayback, setWatchPlayback] = useState<IPlaybackState>({
    currentTime: 0,
    duration: 0,
    isPlaying: false,
    playbackRate: 1,
  });
  // watch 오디오는 여기(플레이어)가 소유하고, OtterTeacher(립싱크)·컨트롤·board가 이 audio를 공유한다.
  const watchAudioRef = useRef<HTMLAudioElement>(null);
  useEffect(() => {
    if (worksheet || phase !== 'watch') return;
    const timer = window.setInterval(() => {
      const studyTime = getActiveTime();
      if (!studentKey || studyTime <= 0) return;
      if (!worksheet)
        recordStudy({
          studentKey,
          studyType: EApiStudyType.CLASSROOM_GRAMMAR,
          languageCode,
          studyTime,
        });
      resetActiveTime(false);
    }, 10_000);
    return () => window.clearInterval(timer);
  }, [worksheet, phase, studentKey, languageCode, getActiveTime, resetActiveTime, recordStudy]);
  const syncWatchPlayback = () => {
    const audio = watchAudioRef.current;
    if (!audio) return;
    setWatchPlayback({
      currentTime: audio.currentTime,
      duration: Number.isFinite(audio.duration) ? audio.duration : 0,
      isPlaying: !audio.paused,
      playbackRate: audio.playbackRate,
    });
  };

  const quizzes: IClassroomGrammarProblem[] = grammar?.practice?.problem ?? [];
  const currentQuiz = quizzes[quizIndex] ?? null;
  const currentChoices = currentQuiz
    ? shuffleArrayWithSeed(currentQuiz.choice, `${choiceShuffleSeedRef.current}-${currentQuiz.key}`)
    : [];
  const watch = worksheet ? null : (grammar?.watch?.ko ?? null);
  const hasWatch = Boolean(watch?.audioLink);

  // 진행(영상 강의 1 + 텍스트 강의 1 + 퀴즈 N) — 영상이 없으면 텍스트 강의부터 시작한다.
  const studyStepCount = hasWatch ? 2 : 1;
  const totalSteps = studyStepCount + quizzes.length;
  const currentStep = phase === 'watch' ? 0 : phase === 'read' ? studyStepCount - 1 : studyStepCount + quizIndex;
  React.useEffect(() => {
    onProgress?.({ current: currentStep, total: totalSteps });
  }, [onProgress, currentStep, totalSteps]);

  // 콘텐츠 없으면(직접 URL 진입 등) 목록으로 복귀 — 로딩 완료 후 grammar를 못 찾은 경우
  React.useEffect(() => {
    if (!worksheet && !isLoading && !grammar) navigate(IYAGIGO_PATH.CLASSROOM_HOME_PATH, { replace: true });
  }, [worksheet, isLoading, grammar, navigate]);

  React.useEffect(() => {
    if (!isLoading && grammar && !grammar.watch?.ko?.audioLink && phase === 'watch') {
      setPhase('read');
    }
  }, [grammar, isLoading, phase]);

  if (!grammar)
    return worksheet ? (
      <TextBlock
        text={isLoading ? '문법을 불러오고 있어요…' : '문법을 불러오지 못했어요. 홈으로 돌아가 다시 시작해 주세요.'}
        design={{ text: { size: 'medium', color: color.GRAY_1 }, box: { padding: '20px' } }}
      />
    ) : null;

  // ############### handler ###############
  const showCompleteModal = (resultCorrectCount: number = correctCount) => {
    if (worksheet) {
      worksheet.onComplete(resultCorrectCount, quizzes.length);
      return;
    }
    if (grammarWorkbook && !grammarWorkbook.studiedAt) {
      modifyWorkbookMutation.mutate({ workbookKey: grammarWorkbook.key, isStudy: true });
    }
    checkAttendance(getAttendanceType(languageCode, 'grammar', 'classroom'));
    const incorrectCount = quizzes.length - resultCorrectCount;
    dispatch(
      modalActions.showContentModal({
        type: 'study-celebration',
        data: {
          title: '오늘도 해냈어요!',
          description: `<strong>${grammar.grammarName?.ko ?? ''}</strong> 학습을 끝까지 마쳤어요`,
        },
      }),
    );
  };

  const handleStartQuiz = () => {
    if (!worksheet)
      recordStudy({
        studentKey,
        studyType: EApiStudyType.CLASSROOM_GRAMMAR,
        languageCode,
        studyTime: getActiveTime(),
      });
    resetActiveTime();

    if (quizzes.length === 0) {
      // 퀴즈 없는 문법이면 카드만 보고 완료
      showCompleteModal();
      return;
    }
    setPhase('practice');
  };

  const handleOpenRead = () => {
    if (!worksheet)
      recordStudy({
        studentKey,
        studyType: EApiStudyType.CLASSROOM_GRAMMAR,
        languageCode,
        studyCount: 1,
      });
    resetActiveTime();
    watchAudioRef.current?.pause();
    setPhase('read');
  };

  const handleOpenWatch = () => {
    if (watchAudioRef.current) {
      watchAudioRef.current.currentTime = 0;
    }
    setPhase('watch');
  };

  const handleSelectChoice = (choiceKey: string) => {
    if (isAnswerView) return;
    setSelectedChoiceKey(choiceKey);
  };

  const handleScoring = () => {
    if (!currentQuiz || selectedChoiceKey === null || isAnswerView) return;
    if (!worksheet)
      recordStudy({
        studentKey,
        studyType: EApiStudyType.CLASSROOM_GRAMMAR,
        languageCode,
        studyTime: getActiveTime(),
      });
    resetActiveTime();
    setIsAnswerView(true);

    const choice = currentQuiz.choice.find((item) => item.key === selectedChoiceKey);
    const isCorrect = choice?.correct ?? false;
    playSound(isCorrect ? 'correct' : 'incorrect');
    if (isCorrect) setCorrectCount((prev) => prev + 1);
  };

  const handleNextQuestion = () => {
    if (quizIndex >= quizzes.length - 1) {
      showCompleteModal();
      return;
    }
    setQuizIndex(quizIndex + 1);
    setSelectedChoiceKey(null);
    setIsAnswerView(false);
  };

  // ############### render 파생값 ###############
  const hasSelection = selectedChoiceKey !== null;
  const isCorrect =
    selectedChoiceKey === null
      ? false
      : (currentQuiz?.choice.find((item) => item.key === selectedChoiceKey)?.correct ?? false);
  const isLastQuiz = quizIndex === quizzes.length - 1;
  const rule = grammar.read?.rule?.ko ?? '';
  const note = grammar.read?.note?.ko ?? '';

  // 문제 빈칸 렌더용 — question/pronunciation의 ___ 를 정답으로 치환 후 markBlanks([정답] → ### 마커).
  // text/후리가나 모두 정답 채워야 글자수 정합 → 후리가나 매핑 정확. blank prop으로 빈 박스↔정답 노출 (cloze 패턴).
  const correctChoice = currentQuiz?.choice.find((choice) => choice.correct);
  const questionWithAnswer = currentQuiz
    ? markBlanks(currentQuiz.question.replace(/_{2,}/, `[${correctChoice?.text ?? ''}]`))
    : '';
  // 후리가나: pronunciation의 ___ 를 정답 choice의 pronunciation(없으면 label)으로 치환
  const pronunciationWithAnswer = currentQuiz?.pronunciation
    ? currentQuiz.pronunciation.replace(/_{2,}/, correctChoice?.pronunciation ?? correctChoice?.text ?? '')
    : undefined;

  // 예문 — 회색 박스. 한자 문장(후리가나 annotation) + 뜻 + 단어 풀이.
  const Examples = (grammar.read?.example ?? []).map((example) => {
    const Vocabularies = example.vocabulary.map((vocabulary) => {
      const hasAnnotation =
        languageCode === EApiLanguageCode.JAPANESE &&
        !!vocabulary.pronunciation &&
        vocabulary.keyword !== vocabulary.pronunciation;
      return (
        <ExampleVocabularyContainer key={`${example.sentence}-${vocabulary.keyword}`}>
          <TextBlock
            text={vocabulary.keyword}
            annotation={hasAnnotation ? { reading: vocabulary.pronunciation } : undefined}
            design={{ text: { size: 'small', color: color.BLACK_2, bold: false }, box: { alignX: 'left' } }}
          />
          <TextBlock
            text={vocabulary.meaning?.ko ?? ''}
            reserveAnnotationSpace={hasAnnotation}
            design={{ text: { size: 'small', color: color.GRAY_1 }, box: { alignX: 'left' } }}
          />
        </ExampleVocabularyContainer>
      );
    });

    return (
      <Canvas key={example.sentence} theme={ETheme.GRAY} shape='round' width='100%'>
        <ExampleContainer>
          <ExampleSentenceContainer>
            <TextBlock
              text={example.sentence}
              annotation={example.pronunciation ? { reading: example.pronunciation } : undefined}
              design={{ text: { size: 'medium', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
            />
            {example.audioLink && (
              <ImageAudioButton
                sources={[example.audioLink]}
                theme={ETheme.GRAY}
                tone='normal'
                kind='outlined'
                size='small'
                shape='round'
                width='35px'
              />
            )}
          </ExampleSentenceContainer>
          <TextBlock
            text={example.translation?.ko ?? ''}
            design={{ text: { size: 'medium', color: color.BLACK_4 }, box: { alignX: 'left' } }}
          />
          <Gap height={'10px'} />
          <VocabulariesContainer>{Vocabularies}</VocabulariesContainer>
        </ExampleContainer>
      </Canvas>
    );
  });

  const Choices = currentChoices.map((choice) => {
    const isSelected = selectedChoiceKey === choice.key;
    return (
      <ChoiceContainer
        key={choice.key}
        $borderColor={getChoiceBorderColor(isAnswerView, isSelected, choice.correct)}
        $answered={isAnswerView}
        onClick={() => handleSelectChoice(choice.key)}>
        <TextBlock
          text={choice.text}
          design={{ text: { size: 'large', color: color.BLACK_2, bold: false }, box: {} }}
        />
      </ChoiceContainer>
    );
  });

  // ############### render ###############
  // conversation-study 패턴: 단일 return + phase별 조건 렌더 (early return 지양)
  return (
    <Wrapper>
      <ScrollContainer>
        <Scroll key={phase} direction='y' height='100%'>
          <ContentContainer>
            {phase === 'watch' && watch?.audioLink && (
              <>
                {/* 수달은 재생 상태만 구독한다. Web Audio 립싱크 복원 시 crossOrigin='anonymous'와 서버 CORS 허용을 함께 검토한다. */}
                <audio
                  ref={watchAudioRef}
                  src={watch.audioLink}
                  preload='metadata'
                  style={{ display: 'none' }}
                  onLoadedMetadata={syncWatchPlayback}
                  onTimeUpdate={syncWatchPlayback}
                  onPlay={syncWatchPlayback}
                  onPause={syncWatchPlayback}
                  onRateChange={syncWatchPlayback}
                  onEnded={syncWatchPlayback}
                />
                <GrammarWatchContainer>
                  <TeacherContainer>
                    <OtterTeacher
                      audioRef={watchAudioRef}
                      onClick={() => {
                        const audio = watchAudioRef.current;
                        if (!audio) return;
                        if (audio.paused) void audio.play().catch(() => undefined);
                        else audio.pause();
                      }}
                    />
                  </TeacherContainer>
                  <RemotionWatchBoard
                    class='grammar'
                    watch={watch}
                    playback={watchPlayback}
                    contentKey={grammar.grammarKey}
                  />
                </GrammarWatchContainer>
              </>
            )}
            {phase === 'read' && (
              <>
                <Canvas theme={ETheme.BROWN} shape='round' width='100%'>
                  <GrammarContainer>
                    <Chip
                      text={grammar.grammarName?.ko ?? ''}
                      size='small'
                      theme={ETheme.BROWN}
                      shape='round'
                      usage='static'
                      kind='filled'
                      width='fit-content'
                    />
                    <Gap height={size.MARGIN_1} />
                    {(grammar.read?.notation || grammar.read?.meaning) && (
                      <GrammarFormContainer>
                        {grammar.read?.notation && (
                          <TextBlock
                            text={grammar.read.notation}
                            design={{
                              text: { size: 'large', color: color.BLACK_2, bold: true },
                              box: { alignX: 'left' },
                            }}
                          />
                        )}
                        <Gap width={size.MARGIN_1} />
                        {grammar.read?.meaning && (
                          <TextBlock
                            text={grammar.read.meaning?.ko ?? ''}
                            design={{
                              text: {
                                size: 'medium',
                                color: color.BROWN_1,
                                bold: true,
                              },
                              box: { alignX: 'left' },
                            }}
                          />
                        )}
                      </GrammarFormContainer>
                    )}
                    <Gap height={size.MARGIN_1} />
                    <TextBlock
                      text='이렇게 써요'
                      design={{
                        text: { size: 'medium', color: color.BROWN_2, bold: true },
                        box: { alignX: 'left' },
                      }}
                    />
                    <TextBlock
                      text={normalizeExplanation(rule, color.BLACK_3)}
                      design={{
                        text: { size: 'medium', color: color.BLACK_3 },
                        box: { alignX: 'left' },
                      }}
                    />
                    {note && (
                      <>
                        <Gap height={size.MARGIN_1} />
                        <TextBlock
                          text='알아두기'
                          design={{
                            text: { size: 'medium', color: color.BROWN_2, bold: true },
                            box: { alignX: 'left' },
                          }}
                        />
                        <TextBlock
                          text={normalizeExplanation(note, color.BLACK_4)}
                          design={{
                            text: { size: 'medium', color: color.BLACK_4 },
                            box: { alignX: 'left' },
                          }}
                        />
                      </>
                    )}
                  </GrammarContainer>
                </Canvas>
                <Gap height={size.MARGIN_1} />
                <ExamplesContainer>
                  <TextBlock
                    text='예문으로 익혀요'
                    design={{
                      text: { size: 'medium', color: color.BLACK_3, bold: true },
                      box: { width: '100%', alignX: 'left' },
                    }}
                  />
                  {Examples}
                </ExamplesContainer>
              </>
            )}
            {phase === 'practice' && currentQuiz && (
              <>
                <Canvas
                  theme={ETheme.PAPER}
                  shape='round'
                  width='100%'
                  padding={`${size.MARGIN_2} 10px`}
                  alignX='center'>
                  <TextBlock
                    text='빈칸에 알맞은 말을 고르세요'
                    design={{ text: { size: 'medium', color: color.BROWN_2 }, box: {} }}
                  />
                  {/* 빈칸 박스 — cloze 패턴. 채점 전 빈 박스, 채점 후 정답 노출(초록). 후리가나도 정답 채운 버전으로 매핑 정합 */}
                  <Gap height={size.MARGIN_2} />
                  {/*<Line direction='horizontal' width='100%' color={color.GRAY_1} margin={`5px 0px 5px 0px`} />*/}
                  <TextBlock
                    text={questionWithAnswer}
                    annotation={pronunciationWithAnswer ? { reading: pronunciationWithAnswer } : undefined}
                    blank={isAnswerView ? { textColor: color.GREEN_4 } : undefined}
                    design={{ text: { size: 'large', color: color.BLACK_2, bold: true }, box: {} }}
                  />
                  <Gap height={size.MARGIN_1} />
                  <TextBlock
                    text={currentQuiz.translation?.ko ?? ''}
                    design={{ text: { size: 'medium', color: color.BLACK_4 }, box: {} }}
                  />
                  {currentQuiz.audioLink && (
                    <>
                      <Gap height={size.MARGIN_1} />
                      <ImageAudioButton
                        sources={[currentQuiz.audioLink]}
                        theme={ETheme.GRAY}
                        tone='normal'
                        kind='outlined'
                        size='small'
                        shape='round'
                        width='45px'
                      />
                    </>
                  )}
                  {/*<Gap height={size.MARGIN_2} />*/}
                  <Line direction='horizontal' width='100%' color={color.GRAY_2} margin={`20px 0px 0px 0px`} />
                  {currentQuiz.vocabulary.length > 0 && (
                    <QuizVocabularySection>
                      <Chip
                        text='어휘'
                        size='tiny'
                        theme={ETheme.BLACK}
                        shape='round'
                        usage='static'
                        kind='filled'
                        width='fit-content'
                      />
                      {currentQuiz.vocabulary.map((vocabulary) => (
                        <QuizVocabularyItem key={vocabulary.keyword}>
                          <TextBlock
                            text={vocabulary.keyword}
                            annotation={vocabulary.pronunciation ? { reading: vocabulary.pronunciation } : undefined}
                            design={{
                              text: { size: 'small', color: color.BLACK_2, bold: false },
                              box: {},
                            }}
                          />
                          <TextBlock
                            text={vocabulary.meaning?.ko ?? ''}
                            reserveAnnotationSpace={vocabulary.keyword !== vocabulary.pronunciation}
                            design={{ text: { size: 'small', color: color.GRAY_1 }, box: {} }}
                          />
                        </QuizVocabularyItem>
                      ))}
                    </QuizVocabularySection>
                  )}
                </Canvas>
                <Gap height={size.MARGIN_1} />
                <ChoicesContainer>{Choices}</ChoicesContainer>
              </>
            )}
          </ContentContainer>
        </Scroll>
      </ScrollContainer>
      <Gap height={size.MARGIN_1} />
      <BottomContainer
        position='relative'
        height={'auto'}
        backgroundColor={color.WHITE_1}
        margin={`0 -${size.PAGE_PADDING}`}
        animationKey={phase === 'practice' ? (isAnswerView ? 'answer' : 'question') : undefined}>
        <BottomContentContainer>
          {phase === 'watch' && watch?.audioLink && (
            <>
              <WatchControl audioRef={watchAudioRef} playback={watchPlayback} />
              <TextButton
                text='다음으로'
                theme={ETheme.BROWN}
                shape='pill'
                size='medium'
                width='100%'
                handleClick={handleOpenRead}
              />
            </>
          )}

          {phase === 'read' && (
            <NavigationContainer>
              <TextButton
                text='연습하기'
                theme={ETheme.BROWN}
                shape='pill'
                size='medium'
                width={hasWatch ? 'calc(100% - 90px)' : '100%'}
                handleClick={handleStartQuiz}
              />
              {hasWatch && (
                <TextButton
                  text='이전으로'
                  theme={ETheme.BROWN}
                  kind='outlined'
                  shape='square'
                  size='medium'
                  width='80px'
                  handleClick={handleOpenWatch}
                />
              )}
            </NavigationContainer>
          )}

          {phase === 'practice' && (
            <>
              {isAnswerView && (
                <ControlRowContainer>
                  <TextButton
                    theme={ETheme.BROWN}
                    size='medium'
                    shape='square'
                    width={NAVIGATION_BUTTON_WIDTH}
                    text={isLastQuiz ? '끝내기' : '다음 문제'}
                    handleClick={handleNextQuestion}
                  />
                </ControlRowContainer>
              )}
              {isAnswerView ? (
                <TextButton
                  theme={isCorrect ? ETheme.GREEN : ETheme.RED}
                  size='medium'
                  shape='pill'
                  width='100%'
                  text={isCorrect ? '정답이에요' : '오답이에요'}
                  icon={isCorrect ? 'circle' : 'close'}
                />
              ) : (
                <TextButton
                  theme={ETheme.BROWN}
                  size='medium'
                  shape='pill'
                  width='100%'
                  text='채점하기'
                  disabled={!hasSelection}
                  handleClick={handleScoring}
                />
              )}
            </>
          )}
        </BottomContentContainer>
      </BottomContainer>
    </Wrapper>
  );
};

export default GrammarStudy;
